import React, { useMemo, useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import { useMsalAuthentication, useAccount, useMsal } from '@azure/msal-react'
import { InteractionType, AccountInfo } from '@azure/msal-browser'
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  Grid,
  Button,
  Card,
  FormControl,
  TextField,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  useSnackbar,
  SelectChangeEvent
} from '@kyc-ui/react'
import useTranslation from 'src/hooks/common/useTranslation'
import useApp from 'src/hooks/app/useApp'
import useTheme from 'src/hooks/common/useTheme'
import { IEnvironment, IIgnoreCaseForm, IIgnoreCasePayload } from 'src/typings/types'
import AppAPI from 'src/api/app'
import { TOKEN } from 'src/constants/storage'
import { currentClientId } from 'src/index'
import { ContainerBox } from 'src/components/BillingPortalForm/BillingPortalForm'

const initialValues: IIgnoreCaseForm = {
  environment: null,
  sharedCustomerId: null,
  caseIds: ''
}

const IgnoreCaseForm = () => {
  const { instance, accounts } = useMsal()
  const { addSnackbar } = useSnackbar()
  const account: AccountInfo | null = useAccount(accounts[0] || {})
  useMsalAuthentication(InteractionType.Redirect)
  const t = useTranslation('usageReport')
  const theme = useTheme()
  const { customers, setToken } = useApp()
  const [customerSearchText, setCustomerSearchText] = useState('')
  const [selectedSharedCustomerId, setSelectedSharedCustomerId] = useState<number | null>(null)
  const [environments, setEnvironments] = useState<IEnvironment[]>([])

  const getEnvironments = async (sharedCustomerId: number) => {
    if (sharedCustomerId) {
      const resp = await AppAPI.getEnviroments(sharedCustomerId)
      setEnvironments(resp?.data ?? [])
    }
  }

  useEffect(() => {
    if (account) {
      instance
        .acquireTokenSilent({
          scopes: [`api://${currentClientId}/Read`],
          account
        })
        .then((response) => {
          Cookies.set(TOKEN, response.accessToken)
          setToken(response.accessToken)
        })
    }
  }, [addSnackbar, instance, account, setToken])

  const validationSchema = useMemo(
    () =>
      yup.object({
        environment: yup.string().required(),
        sharedCustomerId: yup.number().required(),
        caseIds: yup.string().required()
      }),
    []
  )

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: () => void 0
  })

  const disabled =
    !formik.values.environment ||
    !formik.values.sharedCustomerId ||
    !formik.values.caseIds?.trim() ||
    Object.keys(formik.errors).length > 0

  const handleCustomerChange = (event: SelectChangeEvent<number>) => {
    const value = event.target.value
    setSelectedSharedCustomerId(Number(value))
    getEnvironments(Number(value))
    formik.setFieldValue('sharedCustomerId', value)
    formik.setFieldValue('environment', '')
  }

  const handleEnvironmentChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    formik.setFieldValue('environment', Number(value))
  }

  const handleCaseIdsChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value
    const filteredValue = value.replace(/[^0-9,]/g, '')
    formik.setFieldValue('caseIds', filteredValue)
  }

  const handleIgnoreCases = async () => {
    const values = formik.values
    const { sharedCustomerId, caseIds = '' } = values
    try {
      const caseIdsArray = caseIds
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .map((id) => Number(id))

      if (caseIdsArray.length === 0) {
        addSnackbar({
          message: 'Please enter at least one case ID.',
          alertTitle: 'Ignore Cases',
          severity: 'error'
        })
        return
      }
      const payload: IIgnoreCasePayload = {
        environment: Number(formik.values.environment),
        sharedCustomerId: sharedCustomerId ?? null,
        caseIds: caseIdsArray
      }
      const res = await AppAPI.ignoreCases(payload)
      addSnackbar({
        message: res?.data || 'Ignored cases successfully.',
        alertTitle: 'Ignore Cases',
        severity: 'success'
      })
    } catch (error: unknown) {
      const errorMessage = error as { response?: { data?: string }; message?: string }
      addSnackbar({
        message:
          String(errorMessage.response?.data || errorMessage.message) ||
          'Failed to ignore cases, please try again later.',
        alertTitle: 'Ignore Cases',
        severity: 'error'
      })
    }
  }

  return (
    <ContainerBox>
      <form>
        <Card sx={{ px: 4, py: 6 }}>
          <Grid container spacing={6}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="sharedCustomerId" required>
                  {t('customer')}
                </InputLabel>
                <Select
                  required
                  fullWidth
                  id="sharedCustomerId"
                  labelId="customer"
                  name="sharedCustomerId"
                  label={t('customer')}
                  value={formik.values.sharedCustomerId || ''}
                  onChange={handleCustomerChange}
                >
                  <ListSubheader style={{ padding: '16px 16px 0' }}>
                    <TextField
                      size="small"
                      autoFocus
                      autoComplete="off"
                      placeholder="Search"
                      fullWidth
                      onChange={(e) => setCustomerSearchText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Escape') {
                          // Prevents autoselecting item while typing (default Select behaviour)
                          e.stopPropagation()
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                    />
                  </ListSubheader>
                  {customers
                    .filter((customer) => customer.name?.toLowerCase().includes(customerSearchText))
                    .map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="environment" required>
                  {t('environment')}
                </InputLabel>
                <Select
                  required
                  fullWidth
                  id="environment"
                  labelId="environment"
                  name="environment"
                  label={t('environment')}
                  value={formik.values.environment?.toString() || ''}
                  onChange={handleEnvironmentChange}
                  disabled={!selectedSharedCustomerId}
                >
                  {environments.map((env) => (
                    <MenuItem key={env.sharedCustomerId.toString()} value={env.sharedCustomerId.toString()}>
                      {env.name} ({env.type}: {env.sharedCustomerId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <FormControl fullWidth>
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              id="case-ids"
              name="caseIds"
              label="Case IDs"
              value={formik.values.caseIds || ''}
              onChange={handleCaseIdsChange}
              helperText="Enter case IDs separated by commas, no spaces. Only numbers are allowed."
            />
          </FormControl>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Button
                id="ignore-cases-button"
                variant="contained"
                fullWidth
                disabled={disabled}
                color={theme.colorName.button}
                style={{ height: 40 }}
                onClick={handleIgnoreCases}
              >
                {t('ignoreCases')}
              </Button>
            </Grid>
          </Grid>
        </Card>
      </form>
    </ContainerBox>
  )
}

export default IgnoreCaseForm
